import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useInvoices } from '@/hooks/useInvoices';
import { useClients } from '@/hooks/useReference';
import { useBillingNouns } from '@/hooks/useSession';
import { hoursShort, today } from '@/lib/format';
import { Screen } from '@m/ui/Screen';
import { Avatar, Group, Row } from '@m/ui/List';
import { BarButton, Button, Empty, Skeleton, SkeletonList, Stat } from '@m/ui/Controls';
import { StatusPill, isOverdue } from '@m/ui/StatusPill';
import { EntrySheet } from './EntrySheet';
import { NewInvoiceSheet } from './NewInvoice';
import { ClientEditSheet } from './clients/ClientEditSheet';
import { rateLabel, rateOf, useUnbilledByClient, wholeMoney } from './clients/useUnbilled';

/** S12. One client: what is unbilled, what is owed, and the two things to do about it. */
export function ClientDetailScreen() {
  const { id = '' } = useParams();
  const nouns = useBillingNouns();
  const clients = useClients();
  const client = clients.data?.find((c) => c.id === id);
  const unbilled = useUnbilledByClient();
  const sent = useInvoices({ status: 'sent', pageSize: 100 });
  const all = useInvoices({ status: 'all', pageSize: 100 });
  const [editing, setEditing] = useState(false);
  const [logging, setLogging] = useState(false);
  const [invoicing, setInvoicing] = useState(false);

  const u = unbilled.byId.get(id) ?? { hours: 0, amount: 0 };
  const owed = useMemo(() => (sent.data?.items ?? []).filter((i) => i.clientId === id), [sent.data, id]);
  const owedTotal = owed.reduce((s, i) => s + i.totalAmount, 0);
  const overdue = owed.filter(isOverdue).length;
  const invoices = useMemo(
    () => (all.data?.items ?? []).filter((i) => i.clientId === id && i.status !== 'discarded' && i.status !== 'tentative').slice(0, 5),
    [all.data, id]
  );

  if (!client) {
    return (
      <Screen title={nouns.client} back="/clients" backLabel={nouns.clients} large={false}>
        {clients.isPending ? <SkeletonList rows={3} /> : <Empty title={`This ${nouns.clientLower} is gone`} body="It may have been deleted or merged." />}
      </Screen>
    );
  }

  const email = client.billable?.emails?.[0];

  return (
    <Screen
      title={client.name}
      back="/clients"
      backLabel={nouns.clients}
      large={false}
      actions={<BarButton onClick={() => setEditing(true)}>Edit</BarButton>}
    >
      <div className="flex items-center gap-3.5">
        <Avatar id={client.id} name={client.name} size={56} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[26px] leading-8 font-semibold tracking-[-0.02em]">{client.name}</span>
          <span className="truncate text-secondary text-[var(--text-muted)]">
            {client.billable ? `Billed to ${client.billable.name}` : 'Bills itself'}
            {client.status === 'inactive' && ' · Inactive'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {unbilled.isPending ? (
          <Skeleton className="h-[92px] rounded-2xl" />
        ) : (
          <Stat label="Unbilled" value={wholeMoney(u.amount)} caption={`${hoursShort(u.hours)} h`} />
        )}
        {sent.isPending ? (
          <Skeleton className="h-[92px] rounded-2xl" />
        ) : (
          <Stat
            label="Outstanding"
            value={wholeMoney(owedTotal)}
            caption={owed.length ? `${owed.length} sent invoice${owed.length === 1 ? '' : 's'}${overdue ? ` · ${overdue} overdue` : ''}` : 'Nothing owed'}
            tone={owed.length ? 'sent' : undefined}
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button onClick={() => setInvoicing(true)} disabled={u.hours === 0}>
          Invoice time
        </Button>
        <Button variant="secondary" onClick={() => setLogging(true)}>
          Log time
        </Button>
      </div>

      <Group header="Details">
        <Row title="Rate" detail={client.hourlyRate != null ? rateLabel(client.hourlyRate) : `${rateLabel(rateOf(client, unbilled.defaultRate))} (default)`} />
        <Row title={nouns.payer} detail={client.billable?.name ?? 'Bills itself'} />
        <Row title="Invoice email" detail={email ?? 'None'} />
      </Group>

      {invoices.length > 0 && (
        <Group header="Invoices">
          {invoices.map((inv) => (
            <Row
              key={inv.id}
              to={`/invoices/${inv.id}`}
              chevron={false}
              title={inv.invoiceNumber}
              trailing={
                <span className="flex items-center gap-3">
                  <StatusPill status={inv.status} overdue={isOverdue(inv)} />
                  <span className={`tnum text-body ${inv.status === 'paid' ? 'text-[var(--text-muted)]' : ''}`}>{wholeMoney(inv.totalAmount)}</span>
                </span>
              }
            />
          ))}
        </Group>
      )}

      <ClientEditSheet key={editing ? 'open' : 'closed'} open={editing} onClose={() => setEditing(false)} client={client} />
      <EntrySheet
        key={logging ? 'log-open' : 'log-closed'}
        open={logging}
        entry={null}
        defaultDate={today()}
        defaultClientId={client.id}
        onClose={() => setLogging(false)}
      />
      <NewInvoiceSheet open={invoicing} onClose={() => setInvoicing(false)} clientId={client.id} />
    </Screen>
  );
}
